import { useEffect, useRef } from 'react';
import type { DisplayAnnotation } from '@/lib/api/annotations';

interface Props {
  annotations: DisplayAnnotation[];
  orphaned?: DisplayAnnotation[];
  activeId: string | null;
  onAnnotationClick: (ann: DisplayAnnotation) => void;
}

function RailCard({
  ann,
  active,
  orphan,
  onClick,
  setRef,
}: {
  ann: DisplayAnnotation;
  active: boolean;
  orphan: boolean;
  onClick: () => void;
  setRef?: (el: HTMLDivElement | null) => void;
}) {
  return (
    <div
      ref={setRef}
      onClick={onClick}
      className={`cursor-pointer rounded-r-md border-l-[3px] px-2.5 py-1.5 transition-all hover:shadow-md ${
        orphan ? 'opacity-50' : active ? 'bg-blue-50 shadow-md' : 'bg-gray-50'
      }`}
      style={{ borderLeftColor: ann.color }}
    >
      <div className="flex items-start gap-1.5">
        <div
          className="mt-0.5 flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full text-[0.52rem] font-bold text-white"
          style={{ background: ann.color }}
        >
          {ann.initials}
        </div>
        <div className="min-w-0">
          {!ann.isPublic && <span className="mr-0.5 text-[0.65rem]">🔒</span>}
          {orphan && (
            <span className="mr-1 text-[0.6rem] font-semibold uppercase tracking-wide text-amber-500">
              orphaned ·{' '}
            </span>
          )}
          <span className="text-[0.72rem] leading-snug text-gray-700">
            {ann.bodyMd.slice(0, 90)}{ann.bodyMd.length > 90 ? '…' : ''}
          </span>
        </div>
      </div>
    </div>
  );
}

export function MarginNoteRail({ annotations, orphaned = [], activeId, onAnnotationClick }: Props) {
  const activeCardRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    activeCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [activeId]);

  if (annotations.length === 0 && orphaned.length === 0) return null;

  return (
    <div className="hidden w-[210px] shrink-0 flex-col gap-3 overflow-y-auto lg:flex">
      {annotations.map(ann => (
        <RailCard
          key={ann.id}
          ann={ann}
          active={activeId === ann.id}
          orphan={false}
          onClick={() => onAnnotationClick(ann)}
          setRef={activeId === ann.id ? (el) => { activeCardRef.current = el; } : undefined}
        />
      ))}

      {orphaned.length > 0 && (
        <>
          <div className="flex items-center gap-1.5 pt-2 text-[0.62rem] font-bold uppercase tracking-wider text-amber-500">
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
              <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
            </svg>
            Orphaned ({orphaned.length})
          </div>
          {orphaned.map(ann => (
            <RailCard
              key={ann.id}
              ann={ann}
              active={activeId === ann.id}
              orphan
              onClick={() => onAnnotationClick(ann)}
              setRef={activeId === ann.id ? (el) => { activeCardRef.current = el; } : undefined}
            />
          ))}
        </>
      )}
    </div>
  );
}
